import React, {
  ReactNode,
  FC,
} from 'react';
import clsx from 'clsx';
import isFunction from 'lodash/isFunction';
import upperFirst from 'lodash/upperFirst';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import ToolTip from 'core/components/Tooltip';

type TabOption = {
  label: string | ReactNode | FC<object>;
  key: string;
  isDisabled?: (...params: any[]) => boolean;
  disabledTooltip?: ReactNode;
};

type ComponentPart = 'root' | 'tab' | 'active';

type Props = {
  className?: string;
  classes?: Partial<Record<ComponentPart, string>>;
  buttonClassName?: string;
  tabs: TabOption[];
  tabData?: object;
  activeIndex: number;
  onClick: (index: number) => void;
  orientation?: 'vertical' | 'horizontal';
};

export default function NavigationTabs({
  tabs,
  classes: componentClasses,
  className,
  buttonClassName,
  activeIndex,
  onClick,
  orientation = 'horizontal',
  tabData = {},
}: Props) {
  const classes = clsx(
    'populate-tabs',
    className,
    componentClasses?.root,
  );
  const isTabDisabled = (tab: TabOption) => tab.isDisabled?.(tabData) ?? false;
  const handleTabChange = (tabIndex: number) => {
    const selectedTab = tabs[tabIndex];
    if (!selectedTab || isTabDisabled(selectedTab)) return;
    onClick(tabIndex);
  };

  return (
    <Tabs
      className={classes}
      value={activeIndex}
      onChange={(_event, tabIndex: number) => { handleTabChange(tabIndex); }}
      variant="scrollable"
      scrollButtons="auto"
      orientation={orientation}
    >
      {tabs.map((tab, index) => {
        const {
          key,
          label: OptionLabel,
          disabledTooltip,
        } = tab;
        const isComponent = isFunction(OptionLabel);
        const label = isComponent ? <OptionLabel {...tabData} /> : OptionLabel;
        const isActive = activeIndex === index;
        const disabled = isTabDisabled(tab);
        const hasDisabledTooltip = disabled && !!disabledTooltip;
        const tabLabel = hasDisabledTooltip ? (
          <ToolTip title={disabledTooltip}>
            <span className="inline-flex cursor-not-allowed">{label as ReactNode}</span>
          </ToolTip>
        ) : label;
        return (
          <Tab
            key={key}
            className={clsx(
              '!min-h-[32px] !min-w-0 !px-3 !py-1 !rounded-md !text-xs !font-medium !normal-case !border !border-solid !bg-transparent',
              {
                '!border-gray-900 !bg-white !text-gray-900': isActive,
                '!border-transparent !text-gray-500': !isActive,
                '!cursor-not-allowed': disabled,
              },
              buttonClassName,
              componentClasses?.tab,
              { [componentClasses?.active ?? '']: isActive },
            )}
            disabled={disabled && !hasDisabledTooltip}
            aria-disabled={disabled}
            label={tabLabel as ReactNode}
            data-nw-attr={isComponent ? upperFirst(key) : label}
          />
        );
      })}
    </Tabs>
  );
}
